import { useEffect, useRef } from 'react';
import { useAlert } from 'react-alert';
import I18n from 'i18n-js';
// @ts-ignore no .d.ts :(
import querystringify from 'querystringify';

import {
  facebookLogin,
  googleLogin,
  huaweiLogin,
  loginSuccessful,
} from '@lootboy/lib/actions/AuthActions';

import Alert from '~/components/Alert';
import { CircleLoader } from '~/components/atoms/CircleLoader';
import { MenuViewHeader } from '~/components/MenuViewHeader';

import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  APPLE_LOGIN,
  FACEBOOK_LOGIN,
  GOOGLE_LOGIN,
  HUAWEI_LOGIN,
  MENU_PATH,
  STEAM_LOGIN,
  TWITCH_LOGIN,
  TWITTER_LOGIN,
} from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useAppHistory, useAppLocation } from '~/utilities/routing';

import styles from './LoginRedirectPopup.module.scss';

const METHODS_MAPPER = {
  [GOOGLE_LOGIN]: {
    method: googleLogin,
    translation: 'google',
  },
  [FACEBOOK_LOGIN]: {
    method: facebookLogin,
    translation: 'facebook',
  },
  [TWITTER_LOGIN]: {
    translation: 'twitter',
  },
  [STEAM_LOGIN]: {
    translation: 'steam',
  },
  [TWITCH_LOGIN]: {
    translation: 'twitch',
  },
  [HUAWEI_LOGIN]: {
    method: huaweiLogin,
    translation: 'huawei',
  },
  [APPLE_LOGIN]: {
    translation: 'apple',
  },
};

const LoginRedirectPopup = (): JSX.Element => {
  const history = useAppHistory();
  const { search, pathname } = useAppLocation();
  const dispatch = useAppDispatch();
  const alert = useAlert();
  const isProcessing = useRef(false);
  const isLoggedIn = useLoggedIn();

  useEffect(() => {
    if (isProcessing.current || isLoggedIn) {
      return;
    }
    isProcessing.current = true;

    const { code, token, error } = querystringify.parse(search) as Record<
      string,
      string | null
    >;
    const socialLoginData =
      METHODS_MAPPER[pathname as keyof typeof METHODS_MAPPER];

    const errorHandler = (e?: unknown) => {
      const { message } = parseLootBoyError(
        e,
        I18n.t('common_login_error_message'),
      );
      alert.error(
        <Alert
          body={message}
          title={I18n.t(
            `LoginView_${socialLoginData.translation}_login_failed`,
          )}
        />,
      );
      history.replace(MENU_PATH);
    };

    if (error) {
      if (error === 'Api_common_error_user_disabled') {
        errorHandler({ message: I18n.t(error) });
      } else {
        errorHandler();
      }
      return;
    }

    const socialLoginAction =
      'method' in socialLoginData ? socialLoginData.method : undefined;

    dispatch(
      socialLoginAction
        ? socialLoginAction(code ?? '')
        : loginSuccessful(token ?? ''),
    )
      .then(() => {
        alert.success(
          <Alert
            title={I18n.t(
              `LoginView_${socialLoginData.translation}_login_successful`,
            )}
          />,
        );
      })
      .catch(errorHandler);
  }, [alert, dispatch, history, pathname, search, isLoggedIn]);

  return (
    <>
      <MenuViewHeader
        title={I18n.t('MenuView_login_bc')}
        onClose={null}
      />
      <div className={styles.container}>
        <CircleLoader size={75} />
      </div>
    </>
  );
};

export default LoginRedirectPopup;
